// Drilldown chained-fetch hook — the standard way to display a server-resolved
// set of published transactions anywhere in the app. See the `transaction-viewing`
// skill and `.claude/references/drilldown-pattern.md` for the surrounding pattern.
//
// Inputs:
//   transactionIDs — array of transaction.id strings produced by a server-side
//     resolver (e.g. /case_stats/drilldown, /drfa/drilldown). Stable across
//     renders; the hook keys its cache off the list itself so a new ID list
//     triggers a new fetch.
//   isActive — gate the fetch off when the destination view is not in
//     drill-down mode (so the full-feed short-circuit elsewhere in the page
//     can take over without paying for the by-ID call).
//
// Outputs:
//   transactions  — flat array of enriched transactions (Page[] already
//                   flattened) ready to hand to TransactionViewer.
//   mutate        — raw useSWRInfinite mutate (Page[] shape — IDENTICAL to
//                   the full-feed). Pass directly to TransactionViewer's
//                   mutateTransactions; the existing edit hooks
//                   (useUpdateCategoryForTransactions etc.) work unchanged.
//                   No cache-shape adapter required.
//   isLoading     — true until at least one page has resolved.
//   totalCount    — IDs we expect to load (== transactionIDs.length).
//   loadedCount   — flat-array length so far. Lets the caller render a
//                   progressive "Showing M of N" banner under large drilldowns.
//
// IMPORTANT: do not change the by-ID fetch shape without also changing the
// full-feed (api/transactions.js). The whole point of routing both modes
// through useSWRInfinite + {data: [...]} pages is so a single mutate path
// works for both — diverging the shapes brings back the adapter problem
// documented historically in drilldown-pattern.md.

import { useMemo } from 'react';
import {
  useGetEnrichedTransactionsByIDListInfinite,
  flattenInfiniteData,
} from '../api/transactions.js';

export function useTransactionDrilldown({ transactionIDs, isActive }) {
  const idList = Array.isArray(transactionIDs) ? transactionIDs : [];
  const shouldFetch = isActive && idList.length > 0;

  const { data, isLoading, mutate } = useGetEnrichedTransactionsByIDListInfinite(
    idList,
    shouldFetch,
  );

  const transactions = useMemo(() => flattenInfiniteData(data), [data]);

  return {
    transactions,
    mutate,
    isLoading: shouldFetch && isLoading,
    totalCount: idList.length,
    loadedCount: transactions.length,
  };
}
